import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TablePagination,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { api } from "../api/client";
import { VENDOR_CATEGORIES, VENDOR_STATUSES } from "../constants";
import {
  ConfirmDialog,
  FilterBar,
  PageContainer,
  PageHeader,
  TableSkeleton,
} from "../components";
import {
  VendorTable,
  VendorFormDialog,
  LocationSearchDialog,
  VendorAttachmentsDialog,
  PhotoPickerDialog,
} from "../components/vendors";

const WEEKDAY_KEYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const buildDefaultWeekdays = () =>
  WEEKDAY_KEYS.reduce((acc, day) => {
    acc[day] = true;
    return acc;
  }, {});

const initialVendor = {
  name: "",
  branchName: "",
  contactNumber: "",
  address: "",
  longitude: "",
  latitude: "",
  status: "active",
  openTime: "08:00",
  closeTime: "17:00",
  category: "restaurant/eatery",
  notes: "",
  tag: "",
  serviceFeePercent: "",
  commissionPercent: "",
  isFeatured: false,
  isPartner: false,
  isNewPartner: false,
  featuredPhotoUrl: "",
  thumbnailPhotoUrls: [],
};

const createInitialVendor = () => ({
  ...initialVendor,
  weekdays: buildDefaultWeekdays(),
});

function getStoredUser() {
  try {
    const raw = localStorage.getItem("catalog_user");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

export default function VendorsPage({
  vendors,
  setVendors,
  onVendorCreated,
}) {
  const navigate = useNavigate();
  const currentUser = useMemo(() => getStoredUser(), []);
  const isMerchant = currentUser?.userType === "merchant";

  // Form / edit / dialog state
  const [form, setForm] = useState(createInitialVendor);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formOpen, setFormOpen] = useState(false);

  // Listing state
  const [listLoading, setListLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  // Delete confirmation
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  // Location search dialog
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [locationQuery, setLocationQuery] = useState("");
  const [locationCategory, setLocationCategory] = useState("");
  const [locationRadius, setLocationRadius] = useState(10000);
  const [locationCenter, setLocationCenter] = useState({
    latitude: "",
    longitude: "",
  });
  const [locationResults, setLocationResults] = useState([]);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationNotice, setLocationNotice] = useState("");

  // Attachments dialog
  const [attachmentsOpen, setAttachmentsOpen] = useState(false);
  const [attachmentsVendor, setAttachmentsVendor] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [attachmentsUploading, setAttachmentsUploading] = useState(false);

  // Photo picker dialog
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const [photoTarget, setPhotoTarget] = useState("featured");
  const [photoMode, setPhotoMode] = useState("listings");
  const [photoFile, setPhotoFile] = useState(null);
  const [photoUrl, setPhotoUrl] = useState("");
  const [photoUploading, setPhotoUploading] = useState(false);

  // Photo picker — listing browser
  const [listingSearch, setListingSearch] = useState("");
  const [listingResults, setListingResults] = useState([]);
  const [listingLoading, setListingLoading] = useState(false);
  const [listingNotice, setListingNotice] = useState("");
  const [listingSelection, setListingSelection] = useState([]);

  const isValid = useMemo(
    () =>
      form.name.trim() &&
      form.address.trim() &&
      form.latitude !== "" &&
      form.longitude !== "",
    [form],
  );

  // Server-driven fetch when filters / pagination change
  useEffect(() => {
    let active = true;
    setListLoading(true);
    api
      .listCatalogVendors({
        categories: categoryFilter || undefined,
        status: statusFilter || undefined,
        search: searchTerm.trim() || undefined,
        page: page + 1,
        limit: rowsPerPage,
      })
      .then((res) => {
        if (!active) return;
        const next = res?.data?.vendors || [];
        setVendors(next);
        setTotalCount(res?.pagination?.total || 0);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error(err);
      })
      .finally(() => {
        if (active) setListLoading(false);
      });
    return () => {
      active = false;
    };
  }, [setVendors, categoryFilter, statusFilter, searchTerm, page, rowsPerPage]);

  // Filter changes reset to first page
  useEffect(() => {
    setPage(0);
  }, [searchTerm, categoryFilter, statusFilter]);

  // Keep the location-browse center in sync with the form's coords
  useEffect(() => {
    setLocationCenter({
      latitude: form.latitude,
      longitude: form.longitude,
    });
  }, [form.latitude, form.longitude]);

  // Debounced listing-photo search when the photo picker is in listings mode
  useEffect(() => {
    if (!photoDialogOpen || photoMode !== "listings") return;
    let active = true;
    const timer = setTimeout(async () => {
      if (!active) return;
      if (!editingId) {
        setListingResults([]);
        setListingNotice("Save the vendor first to load its listing photos.");
        return;
      }
      setListingLoading(true);
      setListingNotice("");
      try {
        const res = await api.listCatalogListings({
          vendorId: editingId,
          search: listingSearch.trim() || undefined,
          limit: 1000,
          status: "active",
        });
        if (!active) return;
        const list = res?.data?.listings || [];
        const withPhotos = list.filter((item) => item.featuredPhotoUrl);
        setListingResults(withPhotos);
        if (!withPhotos.length) {
          setListingNotice("No listing photos found for this vendor.");
        }
      } catch (err) {
        if (!active) return;
        // eslint-disable-next-line no-console
        console.error(err);
        setListingNotice("Unable to load listing photos.");
      } finally {
        if (active) setListingLoading(false);
      }
    }, 300);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [photoDialogOpen, photoMode, listingSearch, editingId]);

  // === Vendor CRUD ===

  const handleSubmit = async (event) => {
    event?.preventDefault?.();
    if (!isValid) return;
    setLoading(true);
    try {
      const normalizedTags = form.tag
        .split(",")
        .map((tag) => tag.trim())
        .filter(Boolean)
        .join(", ");
      const openTime = form.openTime || "08:00";
      const closeTime = form.closeTime || "17:00";
      const rawServiceFeePercent =
        form.serviceFeePercent === "" || form.serviceFeePercent === null
          ? null
          : Number(form.serviceFeePercent);
      const rawCommissionPercent =
        form.commissionPercent === "" || form.commissionPercent === null
          ? null
          : Number(form.commissionPercent);
      const serviceFeePercent = Number.isFinite(rawServiceFeePercent)
        ? rawServiceFeePercent
        : null;
      const commissionPercent = Number.isFinite(rawCommissionPercent)
        ? rawCommissionPercent
        : null;
      const weeklySchedule = WEEKDAY_KEYS.reduce((acc, day) => {
        const isOpen = Boolean(form.weekdays?.[day]);
        acc[day] = {
          isOpen,
          openTime: isOpen ? openTime : null,
          closeTime: isOpen ? closeTime : null,
        };
        return acc;
      }, {});
      const payload = {
        name: form.name.trim(),
        branchName: form.branchName.trim(),
        contactNumber: form.contactNumber.trim() || null,
        address: form.address.trim(),
        longitude: Number(form.longitude),
        latitude: Number(form.latitude),
        status: form.status,
        operatingHours: `${openTime}-${closeTime}`,
        weeklySchedule,
        category: form.category,
        notes: form.notes?.trim() || null,
        tag: normalizedTags || null,
        serviceFeePercent,
        commissionPercent,
        isFeatured: Boolean(form.isFeatured),
        isPartner: Boolean(form.isPartner),
        isNewPartner: Boolean(form.isPartner && form.isNewPartner),
        featuredPhotoUrl: form.featuredPhotoUrl.trim() || null,
        thumbnailPhotoUrls: Array.isArray(form.thumbnailPhotoUrls)
          ? form.thumbnailPhotoUrls
              .map((url) => (typeof url === "string" ? url.trim() : ""))
              .filter(Boolean)
              .slice(0, 5)
          : [],
      };

      if (editingId) {
        const res = await api.updateCatalogVendor(editingId, payload);
        const updated = res?.data;
        if (updated) {
          setVendors((prev) =>
            prev.map((v) => (v.id === editingId ? updated : v)),
          );
        }
      } else {
        const res = await api.createCatalogVendor(payload);
        const newVendor = res?.data;
        if (newVendor) {
          setVendors((prev) => [newVendor, ...prev]);
          onVendorCreated?.(newVendor);
          navigate("/listings");
        }
      }
      setForm(createInitialVendor());
      setEditingId(null);
      setFormOpen(false);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (vendor) => {
    setEditingId(vendor.id);
    const [openTime, closeTime] = (
      vendor.operatingHours || "08:00-17:00"
    ).split("-");
    const vendorWeeklySchedule =
      vendor.weeklySchedule &&
      typeof vendor.weeklySchedule === "object" &&
      !Array.isArray(vendor.weeklySchedule)
        ? vendor.weeklySchedule
        : null;
    const weekdays = buildDefaultWeekdays();
    let hasWeekdaySchedule = false;
    let scheduleOpenTime = "";
    let scheduleCloseTime = "";
    if (vendorWeeklySchedule) {
      for (const dayKey of WEEKDAY_KEYS) {
        const daySchedule = vendorWeeklySchedule[dayKey];
        if (!daySchedule || typeof daySchedule !== "object") continue;
        if (Object.prototype.hasOwnProperty.call(daySchedule, "isOpen")) {
          weekdays[dayKey] = Boolean(daySchedule.isOpen);
          hasWeekdaySchedule = true;
        }
        if (!scheduleOpenTime && daySchedule.openTime) {
          scheduleOpenTime = String(daySchedule.openTime);
        }
        if (!scheduleCloseTime && daySchedule.closeTime) {
          scheduleCloseTime = String(daySchedule.closeTime);
        }
      }
    }
    const resolvedOpenTime = scheduleOpenTime || openTime || "08:00";
    const resolvedCloseTime = scheduleCloseTime || closeTime || "17:00";
    setForm({
      name: vendor.name || "",
      branchName: vendor.branchName || "",
      contactNumber: vendor.contactNumber || "",
      address: vendor.address || "",
      longitude: vendor.longitude ? String(vendor.longitude) : "",
      latitude: vendor.latitude ? String(vendor.latitude) : "",
      status: vendor.status || "active",
      openTime: resolvedOpenTime,
      closeTime: resolvedCloseTime,
      category: vendor.category || "restaurant/eatery",
      notes: vendor.notes || "",
      tag: vendor.tag || "",
      serviceFeePercent:
        vendor.serviceFeePercent !== undefined &&
        vendor.serviceFeePercent !== null
          ? String(vendor.serviceFeePercent)
          : "",
      commissionPercent:
        vendor.commissionPercent !== undefined &&
        vendor.commissionPercent !== null
          ? String(vendor.commissionPercent)
          : "",
      isFeatured: Boolean(vendor.isFeatured),
      isPartner: Boolean(vendor.isPartner),
      isNewPartner: Boolean(vendor.isPartner && vendor.isNewPartner),
      featuredPhotoUrl: vendor.featuredPhotoUrl || "",
      thumbnailPhotoUrls: Array.isArray(vendor.thumbnailPhotoUrls)
        ? vendor.thumbnailPhotoUrls
        : [],
      weekdays: hasWeekdaySchedule ? weekdays : buildDefaultWeekdays(),
    });
    setFormOpen(true);
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.deleteCatalogVendor(pendingDelete.id);
      setVendors((prev) => prev.filter((v) => v.id !== pendingDelete.id));
      setPendingDelete(null);
    } catch (err) {
      setDeleteError(err?.message ?? "Failed to delete vendor.");
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setForm(createInitialVendor());
    setFormOpen(false);
  };

  const handleOpenNew = () => {
    setEditingId(null);
    setForm(createInitialVendor());
    setFormOpen(true);
  };

  // === Location search ===

  const handleSearchLocations = async () => {
    setLocationLoading(true);
    setLocationNotice("");
    try {
      const res = await api.searchLocations({
        latitude: locationCenter.latitude,
        longitude: locationCenter.longitude,
        query: locationQuery,
        radiusMeters: locationRadius,
        limit: 50,
        category: locationCategory,
      });
      setLocationResults(res?.data || []);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      setLocationNotice("Search failed. Try a different query.");
    } finally {
      setLocationLoading(false);
    }
  };

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      setLocationNotice("Geolocation is not supported in this browser.");
      return;
    }
    setLocationLoading(true);
    setLocationNotice("");

    const requestPosition = () =>
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setLocationCenter({
            latitude: latitude.toFixed(6),
            longitude: longitude.toFixed(6),
          });
          setLocationLoading(false);
        },
        (error) => {
          const message =
            error.code === error.PERMISSION_DENIED
              ? "Location permission denied. Please enable it in your browser settings."
              : "Unable to fetch current location.";
          setLocationNotice(message);
          setLocationLoading(false);
        },
        { enableHighAccuracy: true, timeout: 10000 },
      );

    if (navigator.permissions?.query) {
      navigator.permissions
        .query({ name: "geolocation" })
        .then((status) => {
          if (status.state === "denied") {
            setLocationNotice(
              "Location permission denied. Please enable it in your browser settings.",
            );
            setLocationLoading(false);
            return;
          }
          requestPosition();
        })
        .catch(() => requestPosition());
      return;
    }
    requestPosition();
  };

  const vendorExists = (location) =>
    vendors.some(
      (v) =>
        v.name === location.placeName &&
        String(v.latitude) === String(location.latitude) &&
        String(v.longitude) === String(location.longitude),
    );

  const handlePickLocation = (location) => {
    if (vendorExists(location)) {
      setLocationNotice("Vendor already exists in the current list.");
      return;
    }
    setForm((prev) => ({
      ...prev,
      name: location.placeName || prev.name,
      address: location.address || prev.address,
      latitude: String(location.latitude ?? prev.latitude),
      longitude: String(location.longitude ?? prev.longitude),
    }));
    setLocationDialogOpen(false);
  };

  // === Attachments ===

  const openAttachments = async (vendor) => {
    setAttachmentsVendor(vendor);
    setAttachmentsOpen(true);
    setAttachmentsLoading(true);
    try {
      const res = await api.listCatalogVendorAttachments(vendor.id);
      setAttachments(res?.data?.attachments || []);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setAttachmentsLoading(false);
    }
  };

  const handleUploadAttachment = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !attachmentsVendor) return;
    setAttachmentsUploading(true);
    try {
      const res = await api.uploadCatalogVendorAttachment({
        id: attachmentsVendor.id,
        file,
      });
      const attachment = res?.data?.attachment;
      if (attachment) {
        setAttachments((prev) => [attachment, ...prev]);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setAttachmentsUploading(false);
      event.target.value = "";
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!attachmentsVendor) return;
    if (!window.confirm("Delete this attachment?")) return;
    try {
      await api.deleteCatalogVendorAttachment(
        attachmentsVendor.id,
        attachmentId,
      );
      setAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  // === Photo picker ===

  const openPhotoDialog = (target) => {
    setPhotoTarget(target);
    setPhotoMode("listings");
    setPhotoFile(null);
    setPhotoUrl("");
    setListingSearch("");
    setListingNotice("");
    setListingResults([]);
    setListingLoading(false);
    setListingSelection(() => {
      if (target === "thumbnail") {
        return Array.isArray(form.thumbnailPhotoUrls)
          ? form.thumbnailPhotoUrls.slice(0, 5)
          : [];
      }
      return form.featuredPhotoUrl ? [form.featuredPhotoUrl] : [];
    });
    setPhotoDialogOpen(true);
  };

  const toggleListingSelection = (path) => {
    if (!path) return;
    setListingSelection((prev) => {
      if (prev.includes(path)) return prev.filter((item) => item !== path);
      if (photoTarget === "thumbnail" && prev.length >= 5) {
        setListingNotice("You can select up to 5 thumbnails.");
        return prev;
      }
      return [...prev, path];
    });
  };

  const applyListingSelection = () => {
    if (!listingSelection.length) return;
    if (photoTarget === "featured") {
      setForm((prev) => ({
        ...prev,
        featuredPhotoUrl: listingSelection[0],
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        thumbnailPhotoUrls: listingSelection.slice(0, 5),
      }));
    }
    setPhotoDialogOpen(false);
  };

  const handleUploadPhotoFile = async () => {
    if (!photoFile || photoTarget !== "featured") return;
    setPhotoUploading(true);
    try {
      const res = await api.uploadCatalogPhotoFile({
        file: photoFile,
        photoType: "featured",
      });
      const path = res?.data?.path;
      if (path) {
        setForm((prev) => ({ ...prev, featuredPhotoUrl: path }));
        setPhotoDialogOpen(false);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleImportPhotoUrl = async () => {
    if (!photoUrl.trim() || photoTarget !== "featured") return;
    setPhotoUploading(true);
    try {
      const res = await api.importCatalogPhotoUrl({
        sourceUrl: photoUrl.trim(),
        photoType: "featured",
      });
      const path = res?.data?.path;
      if (path) {
        setForm((prev) => ({ ...prev, featuredPhotoUrl: path }));
        setPhotoDialogOpen(false);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setPhotoUploading(false);
    }
  };

  // === Render ===

  const filterControls = !isMerchant ? (
    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
      <FormControl size="small" sx={{ minWidth: 140 }}>
        <InputLabel>Status</InputLabel>
        <Select
          label="Status"
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value)}
        >
          <MenuItem value="">All statuses</MenuItem>
          {VENDOR_STATUSES.map((status) => (
            <MenuItem key={status} value={status}>
              {status}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: 160 }}>
        <InputLabel>Category</InputLabel>
        <Select
          label="Category"
          value={categoryFilter}
          onChange={(event) => setCategoryFilter(event.target.value)}
        >
          <MenuItem value="">All categories</MenuItem>
          {VENDOR_CATEGORIES.map((category) => (
            <MenuItem key={category} value={category}>
              {category}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Button
        variant="contained"
        startIcon={<AddIcon sx={{ fontSize: 18 }} />}
        onClick={handleOpenNew}
      >
        Add vendor
      </Button>
    </Stack>
  ) : null;

  return (
    <PageContainer>
      <PageHeader
        title="Vendors"
        subtitle={
          isMerchant
            ? "Vendors you can manage."
            : "Manage every vendor in the catalog."
        }
        actions={
          isMerchant ? null : (
            <Button
              variant="contained"
              startIcon={<AddIcon sx={{ fontSize: 18 }} />}
              onClick={handleOpenNew}
              sx={{ display: { sm: "none" } }}
            >
              Add vendor
            </Button>
          )
        }
      />

      <Paper variant="outlined" sx={{ overflow: "hidden" }}>
        {!isMerchant && (
          <FilterBar
            search={searchTerm}
            onSearchChange={setSearchTerm}
            placeholder="Search vendors…"
            actions={
              <Box sx={{ display: { xs: "none", sm: "flex" } }}>
                {filterControls}
              </Box>
            }
          />
        )}
        {listLoading && vendors.length === 0 ? (
          <Box sx={{ p: 1 }}>
            <TableSkeleton rows={5} columns={isMerchant ? 4 : 8} />
          </Box>
        ) : (
          <VendorTable
            vendors={vendors}
            isMerchant={isMerchant}
            onEdit={handleEdit}
            onDelete={setPendingDelete}
            onViewAttachments={openAttachments}
          />
        )}
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, nextPage) => setPage(nextPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50, 100]}
          sx={{ borderTop: 1, borderColor: "divider" }}
        />
      </Paper>

      <VendorFormDialog
        open={formOpen}
        onClose={handleCancelEdit}
        onSubmit={handleSubmit}
        form={form}
        setForm={setForm}
        editingId={editingId}
        loading={loading}
        isMerchant={isMerchant}
        isValid={isValid}
        onOpenLocationBrowse={() => setLocationDialogOpen(true)}
        onOpenPhotoDialog={openPhotoDialog}
      />

      <LocationSearchDialog
        open={locationDialogOpen}
        onClose={() => setLocationDialogOpen(false)}
        locationCenter={locationCenter}
        setLocationCenter={setLocationCenter}
        query={locationQuery}
        setQuery={setLocationQuery}
        category={locationCategory}
        setCategory={setLocationCategory}
        radius={locationRadius}
        setRadius={setLocationRadius}
        results={locationResults}
        loading={locationLoading}
        notice={locationNotice}
        onSearch={handleSearchLocations}
        onUseCurrentLocation={handleUseCurrentLocation}
        onPick={handlePickLocation}
      />

      <VendorAttachmentsDialog
        open={attachmentsOpen}
        onClose={() => setAttachmentsOpen(false)}
        vendor={attachmentsVendor}
        attachments={attachments}
        loading={attachmentsLoading}
        uploading={attachmentsUploading}
        onUpload={handleUploadAttachment}
        onDelete={handleDeleteAttachment}
      />

      <PhotoPickerDialog
        open={photoDialogOpen}
        onClose={() => setPhotoDialogOpen(false)}
        target={photoTarget}
        mode={photoMode}
        setMode={setPhotoMode}
        listingSearch={listingSearch}
        setListingSearch={setListingSearch}
        listingResults={listingResults}
        listingLoading={listingLoading}
        listingNotice={listingNotice}
        listingSelection={listingSelection}
        toggleListingSelection={toggleListingSelection}
        onApplyListings={applyListingSelection}
        photoFile={photoFile}
        setPhotoFile={setPhotoFile}
        photoUrl={photoUrl}
        setPhotoUrl={setPhotoUrl}
        photoUploading={photoUploading}
        onUploadFile={handleUploadPhotoFile}
        onImportUrl={handleImportPhotoUrl}
        editingId={editingId}
      />

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
        submitting={deleting}
        error={deleteError}
        title="Delete vendor?"
        description={
          pendingDelete
            ? `${pendingDelete.name} will be permanently removed from the catalog.`
            : ""
        }
        confirmLabel="Delete"
        confirmColor="error"
      />
    </PageContainer>
  );
}
